import { ColumnDef } from '@tanstack/react-table'
import { GetBatchesDataResponse } from '@/modules/batches/type'
import { format } from 'date-fns'
import { Button } from '@/common/ui/button'
import { Link } from 'react-router-dom'
import { Eye, Pencil } from 'lucide-react'
import { IsActiveBatches } from './components/IsActiveBatches'
import DeleteBatch from './components/DeleteBatch'
import { CouponType } from '@/schema'
import { Badge } from '@/common/ui/badge'
import UpdateBatchCapacityDialog from '../UpdateBatchCapacityDialog'

export const columns: ColumnDef<GetBatchesDataResponse>[] = [
	{
		accessorKey: 'name',
		header: 'ข้อมูลชุดคูปอง',
		cell: ({ row }) => {
			const batch = row.original
			const isSingle = batch.type === CouponType.SINGLE
			const isPromotion = !!batch.promotion?.id
			const requireConnectedLine = (
				batch as unknown as { requireConnectedLine?: boolean }
			)?.requireConnectedLine

			return (
				<div className="space-y-2">
					<div>
						<p className="font-medium">{batch.name}</p>
						{batch.description && (
							<p className="text-xs text-muted-foreground">
								{batch.description}
							</p>
						)}
					</div>

					<div className="flex flex-wrap gap-1.5">
						{isPromotion ? (
							<Badge className="bg-orange-500 text-white hover:bg-orange-500">
								โปรโมชั่น
							</Badge>
						) : (
							<Badge className="bg-green-600 text-white hover:bg-green-600">
								เครดิตฟรี
							</Badge>
						)}
						{isSingle ? (
							<Badge className="bg-blue-600 text-white hover:bg-blue-600">
								คูปองเดี่ยว
							</Badge>
						) : (
							<Badge className="bg-purple-600 text-white hover:bg-purple-600">
								คูปองหลายใบ
							</Badge>
						)}
						{requireConnectedLine ? (
							<Badge variant="default" className="bg-sky-600 hover:bg-sky-600">
								ต้องเชื่อมต่อไลน์
							</Badge>
						) : (
							<Badge variant="outline">ไม่บังคับไลน์</Badge>
						)}
					</div>

					{batch.promotion?.id && (
						<div className="flex items-center gap-1 text-xs text-muted-foreground">
							<span>โปรโมชั่น: {batch.promotion?.name || '-'}</span>
							<Link to={`/operator/deposit-promotion/${batch.promotion.id}`}>
								<Button variant="ghost" size="icon" className="h-6 w-6">
									<Eye className="h-3.5 w-3.5" />
								</Button>
							</Link>
						</div>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'totalCoupons',
		header: 'จำนวนคูปอง',
		cell: ({ row }) => {
			const batch = row.original
			const isSingle = batch.type === CouponType.SINGLE
			const total = isSingle
				? batch.maxQuota == null
					? 'ไม่จำกัด'
					: batch.maxQuota.toLocaleString('th-TH')
				: batch.totalCoupons.toLocaleString('th-TH')
			const used = isSingle ? batch.usedQuota : batch.usedCoupons
			const remaining = isSingle
				? batch.maxQuota == null
					? 'ไม่จำกัด'
					: Math.max(batch.maxQuota - batch.usedQuota, 0).toLocaleString(
							'th-TH',
						)
				: batch.remainingCoupons.toLocaleString('th-TH')

			return (
				<div className="space-y-1 text-sm">
					<p className="font-medium">รวม: {total}</p>
					<p className="text-xs text-muted-foreground">
						ใช้แล้ว: {used.toLocaleString('th-TH')}
					</p>
					<p className="text-xs text-muted-foreground">คงเหลือ: {remaining}</p>
				</div>
			)
		},
	},
	{
		accessorKey: 'updatedAt',
		header: 'สถานะ/เวลา',
		cell: ({ row }) => (
			<div className="space-y-1">
				<IsActiveBatches row={row.original} />
				<p className="text-xs text-muted-foreground">
					สร้าง: {format(new Date(row.original.createdAt), 'dd/MM/yyyy HH:mm')}
				</p>
				<p className="text-xs text-muted-foreground">
					แก้ไข: {format(new Date(row.original.updatedAt), 'dd/MM/yyyy HH:mm')}
				</p>
			</div>
		),
	},
	{
		id: 'actions',
		header: 'จัดการ',
		cell: ({ row }) => (
			<div className="flex flex-wrap items-center gap-1.5">
				<Button variant="ghost" size="icon" asChild>
					<Link to={`/operator/coupons/${row.original.id}`}>
						<Eye className="h-4 w-4" />
					</Link>
				</Button>
				<UpdateBatchCapacityDialog
					batch={row.original}
					trigger={
						<Button variant="ghost" size="icon">
							<Pencil className="h-4 w-4" />
						</Button>
					}
				/>
				<DeleteBatch id={row.original.id} />
			</div>
		),
	},
]
